import {useCallback, useMemo} from 'react';
import {assert} from 'assert-ts';
import {useNavigate} from 'react-router';
import type {ExpressionV4} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {
  useAssertExpressionsEdit,
  useAssertManifestationEdit,
  useAssertSavedWork,
  useMoveManifestationToDuplicateWorkMutation,
} from 'services/metadata';
import {selectMetadataEditOrder} from 'services/metadata/metadataEditSlice';
import {useAppSelector} from 'store/hooks';
import {useStaticAlertDialogAsPromise} from 'components';
import type {
  AlertDialogAsPromiseV3,
  AlertDialogContentProps,
} from 'components/alertdialog/hooks/useAlertDialogAsPromiseV3';
import {useMetadataOperationsContext} from '../contexts/MetadataOperationsContext';
import {getManifestationCountInWork} from '../functions';
import {useMoveToOrCopyFromOtherWork} from './useMoveToOrCopyFromOtherWork';

export const useMoveManifestationContext = (
  manifestationId: string,
): {
  expressions: ExpressionV4[];
  currentExpressionId: string;
  manifestationCountInExpression: number;
  manifestationCountInWork: number;
  moveManifestationToOtherExpression: (
    manifestationId: string,
    expressionId: string,
  ) => void;
  moveManifestationToNewExpression: (manifestationId: string) => void;
  moveManifestationToOtherWork: (manifestationId: string) => void;
  moveManifestationToNewWork: (manifestationId: string) => void;
  ConfirmMoveToOtherWork: AlertDialogAsPromiseV3;
  isConfirmMoveToOtherOpen: boolean;
  isConfirmMoveToOtherClosing: boolean;
  ConfirmMoveToNewWork: AlertDialogAsPromiseV3;
  isConfirmMoveToNewWorkOpen: boolean;
  isConfirmMoveToNewWorkClosing: boolean;
} => {
  const {t} = useLocalization();
  const navigate = useNavigate();
  const workId = useAssertSavedWork().id;
  const {moveManifestationToOtherExpression, moveManifestationToNewExpression} =
    useMetadataOperationsContext();
  const manifestation = useAssertManifestationEdit(manifestationId);
  const expressions = useAssertExpressionsEdit();
  const expressionOrder = useAppSelector(selectMetadataEditOrder);
  const [moveManifestationToDuplicateWorkMutation] =
    useMoveManifestationToDuplicateWorkMutation();

  const {
    openSearch,
    ConfirmMoveToOther: ConfirmMoveToOtherWork,
    isConfirmOpen: isConfirmMoveToOtherOpen,
    isConfirmClosing: isConfirmMoveToOtherClosing,
  } = useMoveToOrCopyFromOtherWork(manifestationId);

  const confirmMoveToNewWorkProps = useMemo(
    (): AlertDialogContentProps => ({
      title: t('page.metadata.manifestation.moveToNewWork.confirm.title'),
      body: t('page.metadata.manifestation.moveToNewWork.confirm.body'),
      cancelTitle: t('general.cancel'),
      okTitle: t('general.move'),
      okIcon: 'Move',
    }),
    [t],
  );
  const {
    pleaseConfirm: confirmMoveToNewWork,
    AlertDialog: ConfirmMoveToNewWork,
    isOpen: isConfirmMoveToNewWorkOpen,
    isClosing: isConfirmMoveToNewWorkClosing,
  } = useStaticAlertDialogAsPromise(confirmMoveToNewWorkProps);

  const handleMoveToNewWork = useCallback(
    (manifestationId: string) => {
      return confirmMoveToNewWork().then(confirmed => {
        if (confirmed) {
          return moveManifestationToDuplicateWorkMutation({
            manifestationId,
            fromWorkId: workId,
          })
            .unwrap()
            .then(newWorkId => {
              navigate(
                `/metadata/${newWorkId}?manifestationId=${manifestationId}`,
              );
            });
        }
        return Promise.resolve();
      });
    },
    [
      confirmMoveToNewWork,
      moveManifestationToDuplicateWorkMutation,
      navigate,
      workId,
    ],
  );

  return useMemo(() => {
    const orderedExpressions = expressionOrder
      .map(group => expressions.find(e => e.id === group.expressionId))
      .filter(e => !!e) as ExpressionV4[];

    const manifestationCountInExpression = assert(
      expressionOrder.find(g => g.expressionId === manifestation.expressionId),
    ).content.filter(c => c.type === 'manifestation').length;

    const manifestationCountInWork =
      getManifestationCountInWork(expressionOrder);

    return {
      expressions: orderedExpressions,
      currentExpressionId: manifestation.expressionId,
      manifestationCountInExpression,
      manifestationCountInWork,
      moveManifestationToOtherExpression,
      moveManifestationToNewExpression,
      moveManifestationToOtherWork: openSearch,
      moveManifestationToNewWork: handleMoveToNewWork,
      ConfirmMoveToOtherWork,
      isConfirmMoveToOtherOpen,
      isConfirmMoveToOtherClosing,
      ConfirmMoveToNewWork,
      isConfirmMoveToNewWorkOpen,
      isConfirmMoveToNewWorkClosing,
    };
  }, [
    ConfirmMoveToNewWork,
    ConfirmMoveToOtherWork,
    expressionOrder,
    expressions,
    handleMoveToNewWork,
    isConfirmMoveToNewWorkClosing,
    isConfirmMoveToNewWorkOpen,
    isConfirmMoveToOtherClosing,
    isConfirmMoveToOtherOpen,
    manifestation.expressionId,
    moveManifestationToNewExpression,
    moveManifestationToOtherExpression,
    openSearch,
  ]);
};
